import {
  createContext,
  Fragment,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styles from './Main.module.scss';

// inject Stripe main script
import '@stripe/stripe-js';

import {
  Switch,
  Route,
  useLocation,
  useRouteMatch,
  useHistory,
  Redirect
} from 'react-router-dom';

import { SFPaper, SFSnackBar, useSFMediaQuery } from 'sfui';

import { Onboarding } from './Onboarding/Onboarding';
import ReportWizard from './ReportWizard/ReportWizard';
import { AnalyticsMain as ContactsAnalytics } from './Analytics/Contacts/AnalyticsMain';
import { PlanUpgrade } from './PlanUpgrade/PlanUpgrade';

import Loader from '../../Components/Loader/Loader';
import {
  AppOptionsContext,
  CustomerContext,
  SubscriptionContext,
  StatesListConfigContext,
  UserContext,
  LARGE_SCREEN,
  Area,
  Customer,
  SFSettings,
  Subscription,
  User,
  getUser,
  getAreas,
  State,
  AGENCY_SUBSCRIPTION_READ,
  AGENCY_SUBSCRIPTION_UPDATE,
  checkPermissions,
  getSubscriptions,
  isPlanConnect,
  isRoleWatcher,
  SETTINGS_CUSTOM_EVENT,
  getCustomer,
  SettingsError,
  useSubscription,
  getAppSubscription,
  AppEnv,
  TimezonesContext,
  getTimezones,
  ToursCarrouselModal,
  Tour,
  MediaContext,
  TourContext,
  TourResumeTab,
  UserSettings,
  getUserSettings,
  useSaveTourAction,
  getAppNotifications,
  AppNotificationsContext,
  UserInvitation,
  getUserInvitation,
  acceptTokenInvitation,
  removeUserInvitation,
  getInvitations,
  isRoleOfficer,
  ReportCustomizationConfig,
  getReportCustomization,
  FeedbackModal,
  hideTours,
  ReminderTooltipType,
  saveFeedback,
  hideSurveys,
  Assistant,
  AssistantAreas,
  getTags,
  WorkspaceConfig,
  getWorkspaces
} from 'ui-smartforce-settings';
import { FullReportProvider } from '../../Context/FullReportContext';

import { RouteMainDefault } from '../../Router/RouteMainDefault';
import { RouteOnboardingGuard } from '../../Router/RouteOnboardingGuard';

import { getStateConfig } from '../../Services/AppService';
import { StateConfigContext } from '../../Context/StateConfigContext';
import {
  handleError,
  isOfficerAlreadyWithAgency,
  isOfficerIdAlreadyExist,
  isOwnerInvited,
  isUserInvitationNotExist
} from '../../Helpers/errors';
import { AmendReport } from './AmendReport/AmendReport';
import { MAIN_ALERT_MESSAGE, MAIN_INIT } from '../../Constants/Events';
import {
  dispatchMainAlertEvent,
  getMainAlertEventData,
  MainAlertEventData
} from '../../Events/MainAlertEvent';
import { OnlineStatusContext } from '../../Context/OnlineStatusContext';
import { NoConnectionModal } from '../../Components/NoConnectionModal/NoConnectionModal';
import {
  USER_REPORTS_AMEND,
  AGENCY_REPORTS_COLLABORATION_READ,
  AGENCY_REPORTS_CREATE,
  AGENCY_REPORTS_READ,
  USER_REPORTS_READ
} from '../../Constants/RolesAndPermissions';
import {
  getCurrentAppByHostname,
  getStateFileName,
  getToursByPlanAndRole,
  getUserSettingsByApp,
  isOfflinePath
} from '../../Helpers';
import { MyReports } from './MyReports/MyReports';
import { CollaboratedReports } from './CollaboratedReports/CollaboratedReports';
import { AgencyReports } from './AgencyReports/AgencyReports';
import { BusinessCardSection } from './BusinessCardSection/BusinessCardSection';
import { MainAd } from '../../Components/MainAd/MainAd';
import { ReportConfig, ReportValue } from '../../Models';
import { CCTopBar } from '../../Components/CCTopBar/CCTopBar';
import {
  isNewVersionUpdated,
  removeNewVersionUpdated
} from '../../Services/SoftwareUpdateService';
import { SoftwareUpdatedModal } from '../../Components/SoftwareUpdatedModal/SoftwareUpdatedModal';
import { TOURS } from '../../Constants/Tours';
import { ActivatePlan } from './ActivatePlan/ActivatePlan';
import { CompleteDebitInformationView } from '../CompleteDebitInformationView/CompleteDebitInformationView';
import { UserInvitations } from './UserInvitations/UserInvitations';
import {
  APP_CONTACTS_API_BASE_URL,
  APP_NEXUS_API_BASE_URL,
  APP_SETTINGS_API_BASE_URL
} from '../../Constants/Url';
import { MainNavMenu } from './MainNavMenu/MainNavMenu';
import { ViewSelectWorkspace } from './ViewSelectWorkspace/ViewSelectWorkspace';
import { IntegrationReportsContext } from '../../Context/IntegrationReportsContext';
import { getReportsFromIntegration } from '../../Services/ReportService';
import { AnalyticsMain as WorkspacesAnalytics } from './Analytics/Workspaces/AnalyticsMain';
import { WorkspacesAgencyReports } from './AgencyReports/Workspaces/WorkspacesAgencyReports';
import { WorkspacesMyReports } from './MyReports/Workspaces/WorkspacesMyReports';

const hideAssistantPaths = [
  '/app/onboarding',
  '/app/user-invitations',
  '/app/plan-upgrade',
  '/app/plan-activate',
  '/app/complete-debit',
  '/app/report/contacts',
  '/app/amend'
];

export interface MainAssistantContextProps {
  setShowAssistant: React.Dispatch<React.SetStateAction<boolean>>;
}

export const MainAssistantContext = createContext<MainAssistantContextProps>({
  setShowAssistant: () => {}
});

type navigationConfiguration = {
  path: string;
  isTopBarBottomTitleVisible?: boolean;
  isTopBarTopTitleVisible?: boolean;
  isFullWidth?: boolean;
  minimalMode?: boolean;
};

const navigationConfigurations: navigationConfiguration[] = [
  {
    path: 'default',
    isTopBarBottomTitleVisible: true,
    isFullWidth: true
  },
  {
    path: 'plan-upgrade',
    isTopBarBottomTitleVisible: false,
    isTopBarTopTitleVisible: false,
    isFullWidth: false,
    minimalMode: true
  },
  {
    path: 'plan-activate',
    isTopBarBottomTitleVisible: false,
    isTopBarTopTitleVisible: false,
    isFullWidth: false,
    minimalMode: true
  },
  {
    path: 'onboarding',
    isTopBarBottomTitleVisible: false,
    isTopBarTopTitleVisible: false,
    isFullWidth: false,
    minimalMode: true
  },
  {
    path: 'user-invitations',
    isTopBarBottomTitleVisible: false,
    isTopBarTopTitleVisible: false,
    isFullWidth: false,
    minimalMode: true
  },
  {
    path: 'complete-debit',
    isTopBarBottomTitleVisible: false,
    isTopBarTopTitleVisible: false,
    isFullWidth: false,
    minimalMode: true
  },
  {
    path: 'settings',
    isTopBarBottomTitleVisible: true,
    isFullWidth: true
  },
  {
    path: 'report',
    isTopBarBottomTitleVisible: false
  },
  {
    path: 'agency-reports',
    isTopBarBottomTitleVisible: true
  },
  {
    path: 'my-reports',
    isTopBarBottomTitleVisible: true
  },
  {
    path: 'collaborated-reports',
    isTopBarBottomTitleVisible: true
  },
  {
    path: 'amend',
    isTopBarBottomTitleVisible: false
  },
  {
    path: 'analytics',
    isTopBarBottomTitleVisible: true
  },
  { path: 'business-card', isTopBarBottomTitleVisible: true }
];

const getNavigationConfig = (
  pathname: string
): navigationConfiguration | undefined => {
  return navigationConfigurations.find(
    (config: navigationConfiguration) => config.path === pathname.split('/')[2]
  );
};

interface MainLocationState {
  section: string | undefined;
  fromLogin?: boolean;
}

const Main = (): ReactElement<{}> => {
  const { url, path } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const { isPhone } = useContext(MediaContext);
  const { user, setUser, userSettings, setUserSettings, setIsOnboarding } =
    useContext(UserContext);
  const { setNotifications } = useContext(AppNotificationsContext);
  const { setTimezones } = useContext(TimezonesContext);
  const { setAreas, setTags, setWorkspaces } = useContext(AppOptionsContext);
  const { setIntegrationReports } = useContext(IntegrationReportsContext);
  const { customer, setCustomer, setReportCustomizationValue } =
    useContext(CustomerContext);
  const { subscriptionsSummary, subscriptions, setSubscriptions } =
    useContext(SubscriptionContext);
  const subscription = useSubscription(getCurrentAppByHostname());
  const { stateConfig, setStateConfig } = useContext(StateConfigContext);
  const { isOnline } = useContext(OnlineStatusContext);
  const { statesList } = useContext(StatesListConfigContext);
  const {
    onStart: onTourStart,
    onEnd: onTourEnd,
    onClose: onTourClose,
    tour: activeTour,
    isFeatureReminderOpen,
    setIsFeatureReminderOpen,
    onDisableReminder,
    onInitPaused
  } = useContext(TourContext);

  const isBigScreen: boolean = useSFMediaQuery(LARGE_SCREEN);

  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<MainAlertEventData>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNavPanelOpen, setIsNavPanelOpen] = useState<boolean>(false);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState<boolean>(false);
  const [isUpdatedModalOpen, setIsUpdatedModalOpen] = useState<boolean>(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
    useState<boolean>(false);
  const [isFeedbackReminderOpen, setIsFeedbackReminderOpen] =
    useState<boolean>(false);
  const [isFeedbackSaving, setIsFeedbackSaving] = useState<boolean>(false);
  const [showAssistant, setShowAssistant] = useState<boolean>(true);
  const [siteTitle, setSiteTitle] = useState<string>('Settings');
  const [navigationConfig, setNavigationConfig] = useState<
    navigationConfiguration | undefined
  >(getNavigationConfig(history.location.pathname));

  const [settingsSelectedSection, setSettingsSelectedSection] = useState<
    string | undefined
  >(location.state ? (location.state as MainLocationState).section : undefined);

  const [isToursCarrouselOpen, setIsToursCarrouselOpen] =
    useState<boolean>(false);

  const [invitations, setInvitations] = useState<UserInvitation[]>([]);

  // Ref to show feature carrousel modal only when user came from login view
  const refFromLogin = useRef<boolean>(
    !!location.state && !!(location.state as MainLocationState).fromLogin
  );

  useSaveTourAction(APP_SETTINGS_API_BASE_URL, 'cc');

  useEffect(() => {
    if (
      hideAssistantPaths.includes(location.pathname) ||
      isPlanConnect(subscription?.plan)
    ) {
      setShowAssistant(false);
    } else {
      setShowAssistant(true);
    }
  }, [location.pathname, subscription]);

  useEffect(() => {
    setSettingsSelectedSection(
      location.state ? (location.state as MainLocationState).section : undefined
    );
  }, [location.state]);

  // Reset settings section when navigating away from settings
  useEffect(() => {
    if (!location.pathname.includes('settings')) {
      setSettingsSelectedSection(undefined);
    }
  }, [location.pathname]);

  useEffect(() => {
    const updateAlertMsg = (e: Event) => {
      const data: MainAlertEventData = getMainAlertEventData(e);
      setAlertData(data);
      setShowSnackbar(true);
    };

    const initData = async (e: Event) => {
      setIsLoading(true);

      try {
        const userData: User = await getUser(APP_SETTINGS_API_BASE_URL);

        setUser(userData);

        const timezones = await getTimezones(APP_SETTINGS_API_BASE_URL);
        setTimezones(timezones);

        const customerData: Customer = await getCustomer(
          APP_SETTINGS_API_BASE_URL
        );
        setCustomer(customerData);

        if (customerData.status === 'Active') {
          const userSettings: UserSettings = await getUserSettings(
            APP_SETTINGS_API_BASE_URL
          );
          setUserSettings(userSettings);

          const appNotifications = await getAppNotifications(
            APP_SETTINGS_API_BASE_URL
          );
          setNotifications(appNotifications);

          if (
            checkPermissions(
              AGENCY_SUBSCRIPTION_READ,
              userData.role.permissions
            )
          ) {
            const subscriptions: Subscription[] = await getSubscriptions(
              APP_SETTINGS_API_BASE_URL
            );
            setSubscriptions(subscriptions);

            const ccSubscription = getAppSubscription(subscriptions, 'cc');

            const nexusSubscription = getAppSubscription(
              subscriptions,
              'nexus'
            );

            // Set tour and surveys settings  only if has cc subscription
            if (ccSubscription) {
              const tourSettings = getUserSettingsByApp(
                userSettings.tours,
                'cc'
              );

              if (tourSettings?.circuit) {
                const pausedTour = TOURS.find(
                  (t) => t.id === tourSettings?.circuit
                );
                if (pausedTour) {
                  onInitPaused(pausedTour);
                }
              }

              const isToursVisible = !tourSettings || tourSettings.show_again;

              if (!isToursVisible) {
                onDisableReminder();
              }

              setIsToursCarrouselOpen(
                !isRoleWatcher(userData.role.id) &&
                  refFromLogin.current &&
                  isToursVisible
              );
            }

            const areas: Area[] = await getAreas(APP_SETTINGS_API_BASE_URL);
            setAreas(areas);

            const tags = await getTags(APP_SETTINGS_API_BASE_URL);
            setTags(tags);

            if (ccSubscription && !isPlanConnect(ccSubscription.plan)) {
              const integrationReports: ReportValue[] =
                await getReportsFromIntegration();
              setIntegrationReports(integrationReports);

              const stateConfig: ReportConfig = await getStateConfig(
                getStateFileName(
                  statesList as State[],
                  customerData?.state_name
                )
              );

              const reportCustomizationValue = await getReportCustomization(
                APP_CONTACTS_API_BASE_URL
              );

              setReportCustomizationValue(reportCustomizationValue);
              setStateConfig(stateConfig);
            }

            if (nexusSubscription) {
              const workspaces: WorkspaceConfig[] = await getWorkspaces(
                APP_NEXUS_API_BASE_URL
              );
              setWorkspaces(workspaces);
            }
          }
        }

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        console.error('Main::initData', e);
        handleError(e, history);
      }
    };

    document.addEventListener(MAIN_ALERT_MESSAGE, updateAlertMsg);
    document.addEventListener(SETTINGS_CUSTOM_EVENT, updateAlertMsg);
    document.addEventListener(MAIN_INIT, initData);

    return () => {
      document.removeEventListener(MAIN_ALERT_MESSAGE, updateAlertMsg);
      document.removeEventListener(SETTINGS_CUSTOM_EVENT, updateAlertMsg);
      document.removeEventListener(MAIN_INIT, initData);
    };
  }, [
    onDisableReminder,
    onInitPaused,
    setAreas,
    setCustomer,
    setNotifications,
    setStateConfig,
    setSubscriptions,
    setTags,
    setTimezones,
    setUser,
    setUserSettings,
    statesList,
    history,
    setReportCustomizationValue,
    setIntegrationReports,
    setWorkspaces
  ]);

  /**
   * Hide alert when leaving route
   */
  useEffect(() => {
    //Snackbar max time displayed
    setTimeout(() => {
      setShowSnackbar(false);
    }, 3600);
  }, [location.pathname]);

  useEffect(() => {
    const getInitialData = async () => {
      try {
        let userData: User = await getUser(APP_SETTINGS_API_BASE_URL);

        if (isRoleOfficer(userData.role.id) && !userData.agency_id) {
          const userInvitation: string | null = getUserInvitation();
          if (userInvitation) {
            const newUser = await acceptTokenInvitation(
              APP_SETTINGS_API_BASE_URL,
              userInvitation
            );

            if (newUser) {
              userData = newUser;
            }
          } else {
            const invitations = await getInvitations(APP_SETTINGS_API_BASE_URL);

            if (invitations.length > 0) {
              setInvitations(invitations);
              history.replace('/app/user-invitations');
            }
          }
        }

        setUser(userData);

        const timezones = await getTimezones(APP_SETTINGS_API_BASE_URL);
        setTimezones(timezones);

        if (userData.agency_id) {
          const customerData: Customer = await getCustomer(
            APP_SETTINGS_API_BASE_URL
          );
          setCustomer(customerData);

          if (customerData.status === 'Active') {
            const userSettings: UserSettings = await getUserSettings(
              APP_SETTINGS_API_BASE_URL
            );
            setUserSettings(userSettings);

            const appNotifications = await getAppNotifications(
              APP_SETTINGS_API_BASE_URL
            );
            setNotifications(appNotifications);

            if (
              checkPermissions(
                AGENCY_SUBSCRIPTION_READ,
                userData.role.permissions
              )
            ) {
              const subscriptions: Subscription[] = await getSubscriptions(
                APP_SETTINGS_API_BASE_URL
              );
              setSubscriptions(subscriptions);

              const ccSubscription = getAppSubscription(subscriptions, 'cc');
              const nexusSubscription = getAppSubscription(
                subscriptions,
                'nexus'
              );

              // Set tour and surveys settings only if has app subscription and its not nexus
              if (ccSubscription) {
                const tourSettings = getUserSettingsByApp(
                  userSettings.tours,
                  'cc'
                );

                if (tourSettings?.circuit) {
                  const pausedTour = TOURS.find(
                    (t) => t.id === tourSettings?.circuit
                  );
                  if (pausedTour) {
                    onInitPaused(pausedTour);
                  }
                }

                const isToursVisible = !tourSettings || tourSettings.show_again;

                if (!isToursVisible) {
                  onDisableReminder();
                }

                setIsToursCarrouselOpen(
                  !isRoleWatcher(userData.role.id) &&
                    refFromLogin.current &&
                    isToursVisible
                );
              }

              const areas: Area[] = await getAreas(APP_SETTINGS_API_BASE_URL);
              setAreas(areas);

              const tags = await getTags(APP_SETTINGS_API_BASE_URL);
              setTags(tags);

              if (ccSubscription && !isPlanConnect(ccSubscription.plan)) {
                const integrationReports: ReportValue[] =
                  await getReportsFromIntegration();
                setIntegrationReports(integrationReports);

                const stateConfig: ReportConfig = await getStateConfig(
                  getStateFileName(
                    statesList as State[],
                    customerData?.state_name
                  )
                );
                const reportCustomizationValue = await getReportCustomization(
                  APP_CONTACTS_API_BASE_URL
                );

                setReportCustomizationValue(reportCustomizationValue);
                setStateConfig(stateConfig);
              }

              if (nexusSubscription) {
                const workspaces: WorkspaceConfig[] = await getWorkspaces(
                  APP_NEXUS_API_BASE_URL
                );
                setWorkspaces(workspaces);
              }
            }
          }
        } else {
          setIsOnboarding(true);
        }

        setIsLoading(false);
        refFromLogin.current = false;

        if (isNewVersionUpdated()) {
          removeNewVersionUpdated();
          setIsUpdatedModalOpen(true);
        }
      } catch (e) {
        if (isOwnerInvited(e) || isUserInvitationNotExist(e)) {
          console.error(`Main::AcceptInvitation::OwnerUsersCanNotBeInvited`, e);
          removeUserInvitation();
        } else if (isOfficerIdAlreadyExist(e)) {
          console.error(`Main::AcceptInvitation::OfficerIdAlreadyExists`, e);
          history.replace('/user-id-error');
        } else if (isOfficerAlreadyWithAgency(e)) {
          console.error(
            `Main::AcceptInvitation::isOfficerAlreadyWithAgency`,
            e
          );
          history.replace('/user-ori-error');
        } else {
          console.error('Main::getInitialData', e);
          handleError(e, history);
        }
      }
    };

    getInitialData();
  }, [
    setUser,
    setUserSettings,
    setNotifications,
    setCustomer,
    setSubscriptions,
    setStateConfig,
    setAreas,
    setTimezones,
    setIsOnboarding,
    onDisableReminder,
    onInitPaused,
    statesList,
    history,
    setReportCustomizationValue,
    setTags,
    setIntegrationReports,
    setWorkspaces
  ]);

  useEffect(() => {
    const unregisterBrowserHistory = history.listen((params) => {
      const { pathname } = params;
      const navigationConfig = getNavigationConfig(pathname);

      if (navigationConfig) {
        setNavigationConfig(navigationConfig);
      } else {
        setNavigationConfig(navigationConfigurations[0]);
      }
    });

    return () => {
      unregisterBrowserHistory();
    };
  }, [history]);

  useEffect(() => {
    // If its offline and
    // it's in onboarding (no customer id or customer not active)
    // or it's watcher role
    if (
      !isOnline &&
      (!user?.agency_id ||
        customer?.status !== 'Active' ||
        isRoleWatcher(user?.role.id))
    ) {
      history.replace('/no-connection');
    } else if (!isOnline && !isOfflinePath(location.pathname)) {
      setIsOfflineModalOpen(true);
    } else {
      setIsOfflineModalOpen(false);
    }
  }, [history, user, customer, location.pathname, isOnline]);

  const onSettingsError = useCallback(
    (e: SettingsError) => {
      handleError(e, history);
    },
    [history]
  );

  const onHome = useCallback(() => history.push('/app'), [history]);

  const onUpgrade = useCallback(
    (product: string) => {
      history.push('/app/plan-upgrade');
    },
    [history]
  );

  const onCloseTourCarrousel = () => {
    setIsFeatureReminderOpen(true);
    setIsToursCarrouselOpen(false);
  };

  const onStartTour = (tour: Tour) => {
    setIsToursCarrouselOpen(false);

    if (tour.id === 1 || tour.id === 2) {
      history.push('/app/settings', { section: 'members' });
    } else if (tour.id === 3) {
      history.push('/app/settings', { section: 'agency' });
    } else if (tour.id === 4) {
      history.push('/app/settings', { section: 'business_card' });
    } else if (tour.id === 5) {
      history.push('/app/settings', { section: 'areas' });
    } else if (tour.id === 6 || tour.id === 7 || tour.id === 8) {
      history.push('/app/agency-reports');
    } else if (tour.id === 9) {
      history.push('/app/settings', { section: 'groups' });
    } else if (tour.id === 10) {
      history.push('/app/my-reports');
      onTourStart(tour);
    } else {
      return;
    }

    onTourStart(tour);
  };

  const onResumeTour = () => {
    onStartTour(activeTour as Tour);
  };

  const onExitTourResume = () => {
    setIsFeatureReminderOpen(true);
    onTourEnd();
  };

  const tours: Tour[] = useMemo(() => {
    const ccSubscription = getAppSubscription(subscriptions, 'cc');
    if (ccSubscription && user) {
      return getToursByPlanAndRole(ccSubscription.plan, user.role.id);
    } else {
      return [];
    }
  }, [subscriptions, user]);

  const onActivate = useCallback(() => {
    history.push('/app/plan-activate');
  }, [history]);

  const onGenerateDebitUrl = useCallback(
    (url: string) => {
      history.push('/app/complete-debit', { url });
    },
    [history]
  );

  const onFeedbackSubmit = (content: string) => {
    try {
      setIsFeedbackSaving(true);
      saveFeedback(
        APP_SETTINGS_API_BASE_URL,
        process.env.REACT_APP_CC_VERSION as string,
        'cc',
        content
      );
      dispatchMainAlertEvent({
        message: 'Thanks! We truly appreciate your feedback.',
        autoHideDuration: 3600
      });
      setIsFeedbackSaving(false);
      setIsFeedbackModalOpen(false);
    } catch (e) {
      setIsFeedbackSaving(false);
      console.error('Main::onFeedbackSumit', e);
      handleError(e, history);
    }
  };

  const onReminderGotIt = (type: ReminderTooltipType, checked: boolean) => {
    if (checked) {
      if (type === 'tours') {
        hideTours(APP_SETTINGS_API_BASE_URL, 'cc');
        onDisableReminder();
      } else {
        hideSurveys(APP_SETTINGS_API_BASE_URL, 'cc');

        setUserSettings((settings) => {
          if (!settings) return settings;

          const newUserSettings: UserSettings = { ...settings };
          const appIndex = newUserSettings.surveys.findIndex(
            (s) => s.app === 'cc'
          );
          if (appIndex === -1) {
            newUserSettings.surveys.push({ app: 'cc', show_again: false });
          } else {
            newUserSettings.surveys.map((s) => {
              if (s.app === 'cc') {
                return { ...s, show_again: false };
              } else return s;
            });
          }
          return newUserSettings;
        });
      }
    }

    setIsFeedbackReminderOpen(false);
    setIsFeatureReminderOpen(false);
  };

  const onFeedbackSkip = () => {
    setIsFeedbackModalOpen(false);
    const surveysSettings = userSettings?.surveys?.find((f) => f.app === 'cc');
    if (!surveysSettings || surveysSettings.show_again) {
      setIsFeatureReminderOpen(false);
      setIsFeedbackReminderOpen(true);
    }
  };

  const onToursOpen = useCallback(() => {
    onTourClose();
    setIsToursCarrouselOpen(true);
  }, [onTourClose]);

  const onFeedbackOpen = useCallback(() => setIsFeedbackModalOpen(true), []);

  const onTitleChange = useCallback(
    (title?: string) => setSiteTitle(title ? title : 'Settings'),
    []
  );

  return (
    <MainAssistantContext.Provider value={{ setShowAssistant }}>
      <SFPaper className={styles.container}>
        {isLoading && <Loader className={styles.loader} />}

        {!isLoading && user && (
          <Fragment>
            <TourResumeTab onExit={onExitTourResume} onResume={onResumeTour} />

            <ToursCarrouselModal
              tours={tours}
              open={!isPhone && isToursCarrouselOpen}
              onClose={onCloseTourCarrousel}
              onStart={onStartTour}
            />

            <div
              className={`${styles.main} ${
                navigationConfig &&
                navigationConfig.isTopBarBottomTitleVisible &&
                !isBigScreen
                  ? styles.showBottomTitle
                  : ''
              } ${navigationConfig?.minimalMode ? styles.isMinimalMode : ''}`}
            >
              {!navigationConfig?.minimalMode && (
                <MainNavMenu
                  className={styles.navPanel}
                  isBigScreen={isBigScreen}
                  isOpen={isNavPanelOpen}
                  user={user}
                  subscriptionsSummary={subscriptionsSummary}
                  isPhone={isPhone}
                  onClose={() => setIsNavPanelOpen(false)}
                  reminderProps={{
                    open: isFeatureReminderOpen || isFeedbackReminderOpen,
                    type: isFeatureReminderOpen ? 'tours' : 'surveys',
                    onGotIt: onReminderGotIt
                  }}
                  onToursClick={onToursOpen}
                  onFeedbackClick={onFeedbackOpen}
                  onTitleChange={onTitleChange}
                />
              )}

              {/* For accessibility reason */}
              <h1 style={{ display: 'none' }}>{siteTitle}</h1>

              <CCTopBar
                className={styles.topBar}
                isMinimal={navigationConfig?.minimalMode}
                siteTitle={siteTitle}
                isTopTitleVisible={navigationConfig?.isTopBarTopTitleVisible}
                isBottomTitleVisible={
                  !isBigScreen
                    ? navigationConfig?.isTopBarBottomTitleVisible
                    : false
                }
                onMenuButtonClick={() => setIsNavPanelOpen(true)}
                onError={onSettingsError}
                reminderProps={{
                  open: isFeatureReminderOpen || isFeedbackReminderOpen,
                  type: isFeatureReminderOpen ? 'tours' : 'surveys',
                  onGotIt: onReminderGotIt
                }}
              />

              <main
                className={`${styles.mainView} ${
                  navigationConfig?.isFullWidth ? styles.fullWidth : ''
                }`}
              >
                <FullReportProvider>
                  <SFSnackBar
                    open={showSnackbar}
                    message={alertData?.message}
                    buttonText={alertData?.actionButtonText}
                    autoHideDuration={alertData?.autoHideDuration ?? 3600}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: isBigScreen ? 'left' : 'center'
                    }}
                    onClose={() => setShowSnackbar(false)}
                  />

                  <NoConnectionModal isOpen={isOfflineModalOpen} />

                  <SoftwareUpdatedModal
                    isOpen={isUpdatedModalOpen}
                    onClick={() => setIsUpdatedModalOpen(false)}
                  />

                  <FeedbackModal
                    isOpen={isFeedbackModalOpen}
                    isSaving={isFeedbackSaving}
                    onSubmit={onFeedbackSubmit}
                    onSkip={onFeedbackSkip}
                  />

                  <Assistant
                    show={showAssistant}
                    items={[{ title: 'Areas', component: <AssistantAreas /> }]}
                  />

                  <MainAd />

                  <Switch>
                    <RouteOnboardingGuard
                      path={`${path}/onboarding`}
                      user={user}
                      customer={customer}
                      subscriptionsSummary={subscriptionsSummary}
                      url={url}
                    >
                      <Onboarding />
                    </RouteOnboardingGuard>

                    <Route path={`${path}/user-invitations`}>
                      <UserInvitations invitations={invitations} />
                    </Route>

                    {checkPermissions(
                      AGENCY_REPORTS_READ,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/agency-reports/contacts`}>
                        <AgencyReports />
                      </Route>
                    )}

                    {!!subscriptionsSummary.appPlan.nexus && (
                      <Route
                        path={`${path}/agency-reports/workspaces/:workspaceId`}
                      >
                        <WorkspacesAgencyReports />
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_REPORTS_READ,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/agency-reports`}>
                        {!subscriptionsSummary.appPlan.nexus ? (
                          <Redirect
                            to={{
                              pathname: `${path}/agency-reports/contacts`
                            }}
                          />
                        ) : (
                          <ViewSelectWorkspace
                            path={`${path}/agency-reports`}
                          />
                        )}
                      </Route>
                    )}

                    {checkPermissions(
                      USER_REPORTS_READ,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/my-reports/contacts`}>
                        <MyReports />
                      </Route>
                    )}

                    {!!subscriptionsSummary.appPlan.nexus && (
                      <Route
                        path={`${path}/my-reports/workspaces/:workspaceId`}
                      >
                        <WorkspacesMyReports />
                      </Route>
                    )}

                    {checkPermissions(
                      USER_REPORTS_READ,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/my-reports`}>
                        {!subscriptionsSummary.appPlan.nexus ? (
                          <Redirect
                            to={{
                              pathname: `${path}/my-reports/contacts`
                            }}
                          />
                        ) : (
                          <ViewSelectWorkspace path={`${path}/my-reports`} />
                        )}
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_REPORTS_COLLABORATION_READ,
                      user?.role?.permissions
                    ) && (
                      <Route path={`${path}/collaborated-reports`}>
                        <CollaboratedReports />
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_REPORTS_CREATE,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/report/contacts`}>
                        <ReportWizard />
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_REPORTS_CREATE,
                      user.role?.permissions
                    ) && (
                      <Route path={`${path}/report`}>
                        <ViewSelectWorkspace path={`${path}/report`} />
                      </Route>
                    )}

                    <Route path={`${path}/analytics/contacts`}>
                      <ContactsAnalytics />
                    </Route>

                    {!!subscriptionsSummary.appPlan.nexus && (
                      <Route path={`${path}/analytics/workspaces/:workspaceId`}>
                        <WorkspacesAnalytics />
                      </Route>
                    )}

                    <Route path={`${path}/analytics`}>
                      {!subscriptionsSummary.appPlan.nexus ? (
                        <Redirect
                          to={{
                            pathname: `${path}/analytics/contacts`
                          }}
                        />
                      ) : (
                        <ViewSelectWorkspace path={`${path}/analytics`} />
                      )}
                    </Route>

                    {checkPermissions(
                      USER_REPORTS_AMEND,
                      user?.role?.permissions
                    ) && (
                      <Route path={`${path}/amend`}>
                        <AmendReport />
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_SUBSCRIPTION_UPDATE,
                      user?.role?.permissions
                    ) && (
                      <Route path={`${path}/plan-upgrade`}>
                        <PlanUpgrade />
                      </Route>
                    )}

                    {checkPermissions(
                      AGENCY_SUBSCRIPTION_UPDATE,
                      user?.role?.permissions
                    ) && (
                      <Route path={`${path}/plan-activate`}>
                        <ActivatePlan />
                      </Route>
                    )}

                    <Route path={`${path}/business-card`}>
                      <BusinessCardSection />
                    </Route>

                    <Route path={`${path}/settings`}>
                      <SFSettings
                        product="cc"
                        enviroment={process.env.REACT_APP_ENV as AppEnv}
                        stripeApiKey={`${process.env.REACT_APP_STRIPE_KEY}`}
                        selectedSectionName={settingsSelectedSection}
                        reportCustomizationConfig={
                          stateConfig?.report_customization as ReportCustomizationConfig
                        }
                        onSectionChange={(name) =>
                          setSettingsSelectedSection(name)
                        }
                        onError={onSettingsError}
                        onHome={onHome}
                        onUpgrade={onUpgrade}
                        onActivate={onActivate}
                        onGenerateDebitUrl={onGenerateDebitUrl}
                      />
                    </Route>

                    <Route path={`${path}/complete-debit`}>
                      <CompleteDebitInformationView />
                    </Route>

                    {/* Default path */}

                    <RouteMainDefault
                      path={`${path}/`}
                      url={url}
                      user={user}
                      subscription={subscription}
                      subscriptionsSummary={subscriptionsSummary}
                    />

                    <RouteMainDefault
                      path={`${path}/*`}
                      url={url}
                      user={user}
                      subscription={subscription}
                      subscriptionsSummary={subscriptionsSummary}
                    />
                  </Switch>
                </FullReportProvider>
              </main>
            </div>
          </Fragment>
        )}
      </SFPaper>
    </MainAssistantContext.Provider>
  );
};

export default Main;
